import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { Download, Search, Sparkles, X } from "lucide-react";
import { Input } from "./ui/input";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import { exportToCSV } from "@/services/helpers";
import { Spinner } from "./ui/spinner";
import { type Table } from "@tanstack/react-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

type SearchExportGenerate = {
  search: string;
  data: any;
  showButton?: boolean;
  tab: string;
  setSearch: (s: string) => void;
  isGeneratedLoading: boolean;
  refetch: () => void;
  suggestDisabled?: boolean;
  suggestTooltipMessage?: string | null;
  onSuggestTooltipOpenChange?: (open: boolean) => void;
  table: Table<any>;
};

const SearchExportGenerate = ({
  table,
  data,
  tab,
  showButton = true,
  refetch,
  search,
  setSearch,
  isGeneratedLoading,
  suggestDisabled = false,
  suggestTooltipMessage,
  onSuggestTooltipOpenChange,
}: SearchExportGenerate) => {
  const { t } = useLocale();
  const suggestButton = (
    <Button
      variant="outline"
      className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
      onClick={() => refetch()}
      disabled={isGeneratedLoading || suggestDisabled}
    >
      <Sparkles />
      <LocalizedText id="legacy.suggest.new.prompts.yax7re" />
      {isGeneratedLoading ? <Spinner></Spinner> : null}
    </Button>
  );

  return (
    <>
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder={t("common.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-8 pr-8 max-w-sm min-w-[200px]"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-0 cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>
      {(tab !== "suggested" || (tab === "suggested" && showButton)) && (
        <Separator
          orientation="vertical"
          className="data-[orientation=vertical]:h-[30px] mx-3"
        />
      )}
      {tab !== "suggested" && (
        <Button
          className="cursor-pointer"
          variant="outline"
          onClick={() =>
            exportToCSV(
              data,
              table.getSelectedRowModel().rows.map((r: any) => r.original) ||
                [],
              "prompts.csv",
              {
                emptyMessage: t("legacy.there.is.no.data.for.export.ojj9kv"),
                successMessage: t("legacy.csv.is.downloaded.kdpi5p"),
              },
            )
          }
        >
          <Download></Download> <LocalizedText id="legacy.export.1305875" />
        </Button>
      )}
      {tab === "suggested" &&
        showButton &&
        (suggestDisabled && suggestTooltipMessage ? (
          <Tooltip onOpenChange={onSuggestTooltipOpenChange}>
            <TooltipTrigger asChild>
              <span className="inline-flex">{suggestButton}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-center">
              {suggestTooltipMessage}
            </TooltipContent>
          </Tooltip>
        ) : (
          suggestButton
        ))}
    </>
  );
};

export default SearchExportGenerate;
